
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CONFIRMATION_MODE, CONFIRMATION_RESULT, WIZ_PHYSIC_POSITION, WizMathUtils } from '@wizeoo/akm-core-lib';
import { PopUpManager, WizIcon, WizPopUp, WizRoot, WizVgroup, WizEventManager, WizEvent, WizTranslationManager, ConfirmationResultObj } from '@wizeoo/akm-component-lib';
import { COMPUTE_MODE, EM_ACTION, FIELD_KIND, WizConstants } from '@wizeoo/akm-compute-lib';
import { DataPack, FreeDataValue, WidgetConfigurationItemObj, WidgetSelect } from '@wizeoo/akm-message-lib';

import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { ViewData } from '../../../model/view.data';
import { FreedataArgsVO } from '../../../vo/core/freedataargs.vo';
import { FreedataValueVO } from  '../../../vo/core/freedatavalue.vo';
import { PopUtils } from '../../../framework/components/pop/pop-utils';
import { IWidget } from '../../../manager/widgets/i-widget';
import { FreeDataVO } from '../../../vo/core/freedata.vo';
import { FastSelectData } from './../../../manager/widgets/widget.engine';
import { AlkEvent } from '../../../framework/events/alk.event';
import { FreedataWidgetBasic } from '../base/freedata-widget-basic';
import { GridsterWidgetItem } from '../../../vo/workingzone/gridster-widget-item';
import { PivotField } from '../../../manager/baselines/pivot-field.obj';
import { ArgEditorData } from '../freedatas/renderers/args.renderer';
import { DeleteFreedataValueParams, FreedataGridDeletePop } from '../freedatas/pops/freedata-grid-delete.pop';
import { FeiVO } from '../../../vo/workingdata/fei.vo';
import { TriggerMatrixItem } from '../../../manager/computer/freedata.formula.core.engine';
import { FreedataFormulaCoreEngine } from '../../../manager/computer/freedata.formula.core.engine';
import { FreedataFormSettingsPop } from '../freedatas/pops/freedata-form-settings.pop';
import { FreeDataValueUnicityCmd } from '../../../command/core/freedatas/freedatavalue.unicity.cmd';
import { UpdateEntry, CompositeSourcePack } from '../../../command/core/sync/sync.service';
import { FreedataFormChoosePop, FreedataFormChooserVO } from './renderers/freedata-form-choose.pop';
import { FreedataSeqCmd } from '../../../command/core/freedatas/freedataseq.cmd';

@Component({
  selector: 'freedata-form-widget',
  templateUrl: './freedata-form.widget.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** formulaire de saisie d'un nouveau FDV */
export class FreedataFormWidget extends FreedataWidgetBasic implements AfterViewInit, OnDestroy {
  
  constructor(elementRef: ElementRef, protected cdref: ChangeDetectorRef) {
    super(elementRef);
    this.widgetKind = WIDGET_KIND.FD_FORM;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.FREEDATA;
    this.settingsPopClassFactory = FreedataFormSettingsPop;
  }

  /* ##############################################################################
                  composants spécifiques
  ############################################################################## */

  // zone d'ajout des champs de saisie
  @ViewChild('fieldsContainer', { read: ViewContainerRef, static: true }) fieldsContainer: ViewContainerRef;
  // @ViewChild('kindLbl') kindLbl: WizLabel; // libellé du mode en cours (create, update, mass)
  @ViewChild('icoSelect', { read: WizIcon }) public icoSelect: WizIcon;
  @ViewChild('icoDelete', { read: WizIcon }) public icoDelete: WizIcon;
  @ViewChild('argsZone', { read: WizVgroup }) argsZone: WizVgroup; // zone d'édition des args

  public get specifMode(): boolean {
    // let realWidth: number = 
    // console.log(this.elementRef?.nativeElement?.offsetWidth);
    return WizRoot.specifMode || this.elementRef?.nativeElement?.offsetWidth < 300;
  }

  public get gatlingMode():boolean {
    return !!this.context?.gatling && !this.templatingMode;
  }

  public freedata: FreeDataVO = null; // le freedata porteur

  public currentFDV: FreedataValueVO = null;
  public clone: FreedataValueVO = null; // clone du FDV en cours de modif

  public get cloneExist():boolean {
    return !!this.clone?.exist;
  }

  public fakeFei: FeiVO = null; // fei Fake pour gestion des contrôles de cohérence
  /** 
   * la matrice de résolution des dépendances (triggers) pour jouer les cascades
   * code source = TriggerMatrixItem. Les children portent les éléments à rejouer en cascade
   */
  private matrix: Map<string, TriggerMatrixItem> = null;
  // la map des FreedataArgsVO pour le FD arg.Code = FreedataArgsVO
  private fieldsMap: Map<string, FreedataArgsVO> = new Map(); 
  // champs obligatoires de base
  private baseRequiredFields: string[] = [WizConstants.FIELD_CODE, WizConstants.FIELD_SHORT_CODE, WizConstants.FIELD_LABEL]; 
  // // champs obligatoires si level > 1
  // private mayBeFields: string[] = [WizConstants.FIELD_PARENT_CODE]; 
  // liste des champs obligatoires de base + MANDATORY dans Args
  public requiredFields: Set<string> = null; 

  public widgetItems: GridsterWidgetItem[]; // items de travail


  /* ##############################################################################
                  paramètres spécifiques
  ############################################################################## */

  private _showSelect: boolean = true;
  // autoriser la zone de sélection
  @Input() public set showSelect(value: boolean) {
    this._showSelect = value;
    this.cdref.markForCheck();
  }
  public get showSelect(): boolean {
    return this.settings.showR && this._showSelect; // this._showSelect;
  }

  private _showAdd: boolean = true;
  // autoriser la zone d'ajout
  @Input() public set showAdd(value: boolean) {
    this._showAdd = value;
    this.cdref.markForCheck();
  }
  public get showAdd(): boolean {

    // prise en compte d'un filtre global obligatoire manquant
    let emptyMand: boolean = this.mandatoryGlobalEmpty(); 
    // console.log('showAdd', emptyMand);
    return this.settings.showC && this._showAdd && !emptyMand;
  }

  private _showDelete: boolean = true;
  // autoriser la zone de suppression
  @Input() public set showDelete(value: boolean) {
    this._showDelete = value;
    this.cdref.markForCheck();
  }
  public get showDelete(): boolean {
    return this.settings.showD && this._showDelete && this.cloneExist;
  }

  private _hasUpdate: boolean = false; // modif sur l'item en cours, sinon on sauvegarde pas
  public get hasUpdate(): boolean {
    return this._hasUpdate;
  }
  public set hasUpdate(value: boolean) {
    this._hasUpdate = value;
    WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_STATE, [this.uid, this._hasUpdate])); // dispatch de l'event pour controle par les compos
  }
  public saveDisabled: boolean = true; // par défaut, on sauvegarde pas. Tous les champs doivent être OK
  public errorSet: Map<string, string> = new Map(); // sur checkSave, contrôle des valeurs de champs
  public restrictSet: Map<string, string> = new Map(); // sur checkSave, contrôle des champs non éditables (masqués)
  public lockSet: Map<string, string> = new Map(); // sur checkSave, contrôle des champs non éditables (simple locl)
  public useShortCodeAsCode: boolean = true; // le code n'est pas utilisé dans le form, on prenf

  /** visibilité des bts save / cancel */
  public get editMode(): boolean  {
    return !!this._context?.eda;
  }
  /**
   * mode création
   */
  public get creationMode(): boolean  {
    return !!this.currentFDV && !this.currentFDV.exist;
  }

  /** annule la sélection sur un formulaire */
  @Input() public showRevert: boolean = true;

  /* ##############################################################################
        sélection d'un élément existant
  ############################################################################## */

  /** popUp de sélection d'un élément */
  private choosePop: WizPopUp;

  /** sélection depuis la dropDown */
  public askForSelect(e: Event): void {
    if (PopUpManager.exist(this.choosePop)) {
      // suppression de l'instance existante
      this.choosePop.hide();
    }

    // construction des données pour la popUp de sélection d'élément
    const datas: FreedataFormChooserVO = {
      context: this._context, // la configuration
      updateCallback: this.backFromSelectionPop.bind(this), // la callback pour retour
      pivot: this, // le pivot source pour prise en compte du mode "conf"
      sourceDS: this.fdvDS // la liste source
    }

    // ouverture de la pop. Data[fei, context, WizbaseCollector, PivotField, callback]
    this.choosePop = new WizPopUp(
      FreedataFormChoosePop,
      true,
      false,
      WIZ_PHYSIC_POSITION.MIDDLE_CENTER,
      FreedataFormChoosePop.baseWidth + 'px',
      FreedataFormChoosePop.baseHeight + 'px'
    );


    
    // alimentation des datas
    this.choosePop.datas = datas;

    const pos: DOMRect = this.icoSelect.elementRef.nativeElement.getBoundingClientRect();

    const bestXY: [number, number] = PopUtils.setPopCoord(pos, this.choosePop, WIZ_PHYSIC_POSITION.BOTTOM_RIGHT);
    const x: string = bestXY[0] + 'px';
    const y: string = bestXY[1] + 'px';
    const targetPosition: any = { left: x, top: y }; // position physique
    this.choosePop.customPosition = targetPosition;
    this.choosePop.show();
  }

  /**
   * retour de popUp de sélection du formulaire
   * @param fdv 
   */
  private backFromSelectionPop(fdv: FreedataValueVO): void {
    if(!!fdv) {
      this.doSelect(fdv);
    }
  }

  /** suppression autorisée */
  public mayAdd(): boolean {
    return true; // this.singleEditMode;
  }

  /** avant ajout, message si modifs en cours */
  public beforeAskForAdd(): void {
    
    if (this.hasUpdate) {
        // True : le modèle à changé
        // ouverture de la popup
        // l'Observer est passé en tant que data
        WizPopUp.confirm(
          '$.cancel.update.pop.title.$',
          '$.cancel.update.pop.content.$',
          this.doUnselect.bind(this),
          CONFIRMATION_MODE.CONFIRM_CANCEL,
          null
        ).show();
      } else {
        // if there is no modification
        let res:ConfirmationResultObj = new ConfirmationResultObj();
        res.result = CONFIRMATION_RESULT.CONFIRM;
        this.askForAdd(res);
    }
  }

  /** click sur l'icone de création */
  public askForAdd(confirmationResult: ConfirmationResultObj): Promise<void> {
    if (confirmationResult.result !== CONFIRMATION_RESULT.CONFIRM) {
      return;
    }
    
    const fd = this.model.freedataEngine.getFreeData(this.freedataCode);

    // en cas de master unique, prise des éléments de clé (parentCode)
    let parentVO: FreedataValueVO = this.model.widgetEngine.findMasterParentUnicity(this);
    
    if(!!parentVO && parentVO.level === this.level) {
        // asservi sur même level, c'est pas un parent, en fait...
        // on va prendre le papa du "frère"
        parentVO = parentVO.getParentVO();
    }
    
    const newFdv = this.model.freedataEngine.createFreeDataValue(fd, parentVO);
    // on s'assure que le level est correct (asservi même niveau et pas de parent)
    newFdv.level = this.level;

    // contrôle des champs requis dans le formulaire
    // si pas présent, alimentation par clé de Hash
    let fieldMap: Map<string, WidgetConfigurationItemObj> =  new Map(this.context.ff.map(item => [item.fdvf, item]));
    // console.log('fieldMap', fieldMap, this.fieldsMap);
    //let newHash: string = WizMathUtils.createUUID();
    // let newSequence: string = null;
    let needSequence: boolean = !!fd.autoCode;
    // recherche de champ obligatoire non alimenté
    if (!needSequence) {
      this.baseRequiredFields.forEach(f => {
        if(!fieldMap.has(f)) {
          needSequence = true;

          // console.log('update', f, newHash);
          //this.model.freedataEngine.feedFreedataValueField(newFdv, f, newHash );
        }
      });
    }

    if(needSequence) { // d'abord on chope la séquence
      new FreedataSeqCmd(this.model).getSequence(newFdv, this.askForAddAfterSequence.bind(this)).execute();
      return;
    } 

    // console.log('feeded', newFdv);
    if(this.gatlingMode) {
      this.gatlingLocalFeed(newFdv);
    }

    this.doSelect(newFdv);
  }

  /**
   * construction asynchrone sur appel de séquence
   * @param cmd 
   */
  private askForAddAfterSequence(cmd: FreedataSeqCmd): void {
    
    let newFdv = cmd.fdv;
    let newSequence: string = cmd.nextID.toString();
    let fieldMap: Map<string, WidgetConfigurationItemObj> =  new Map(this.context.ff.map(item => [item.fdvf, item]));
    // recherche de champ obligatoire non alimenté
    this.baseRequiredFields.forEach(f => {
      if(!fieldMap.has(f)) {
        //console.log('fieldMap', f, newSequence);
        this.model.freedataEngine.feedFreedataValueField(newFdv, f, newSequence );
      }
    });

    // console.log('feeded', newFdv);
    if(this.gatlingMode) {
      this.gatlingLocalFeed(newFdv);
    }

    this.doSelect(newFdv);
  }


  /**
   * en mode gatling, ajout éventuel des champs depuis le localStorage
   * @param newFdv 
   */
  private gatlingLocalFeed(newFdv: FreedataValueVO): void {
    

    let excludeSet: Set<string> = new Set();
    let inSet: Set<string> = new Set();

    let localClone: FreeDataValue  = JSON.parse(localStorage.getItem('gatlingParams') ?? '{}');
    let oldDie: FreedataValueVO = null;
    if(!!localClone?.code) {
      oldDie = this.model.freedataEngine.getFreedataValue(localClone.freeDataCode, localClone.code);
    }
    
    if(!!oldDie) {
      //prise des champs exclus du clonage
      this.context.ff.forEach(wcio => {
        if(wcio.noc) {
          excludeSet.add(wcio.fdvf);
        } else {
          inSet.add(wcio.fdvf);
        }
      });
    }

    [...inSet.keys()].forEach(s => {
      // modification du champ cible
      this.model.freedataEngine.feedFreedataValueField(newFdv, s, oldDie.argsObjVO[s]);
    });
    // console.log('gatlingLocalFeed', newFdv);
  }


  /** selection par 2x clic sur un grid externe */
  public fastSelect(e: AlkEvent): void {
    let data: FastSelectData = e.data;
    const sourceFei: FeiVO = data.fei;
    if(data.kind === WIDGET_GLOBAL_KIND.FREEDATA) {
      const fdv: FreedataValueVO = data.data as FreedataValueVO; 
      
      if(this.state >= WIDGET_STATE.READY) {
        if(!!fdv) {
          const fdKey: string = fdv.freeDataCode + ViewData.CONTEXT_SEPARATOR + fdv.level;
          // console.log('fastSelect', this.context.fdk, fdKey, fdv);
          if(fdKey === this.context.fdk) { // vérifier que c'est le même FD et même Level
            this.doSelect(fdv);
          }
        } else if(data.freedataCode === this.freedataCode && data.freedataLevel === this.level) {
          let res:ConfirmationResultObj = new ConfirmationResultObj();
          res.result = CONFIRMATION_RESULT.CONFIRM;
          this.askForAdd(res);
        }
      }
    } 
    // ViewData.LAST_DOUBLE_EVENT = null;
  }

  /** 
   * injection depuis calendar.addPop
   * @param e alkEvent.PIVOT_DOUBLE
   * data = [fei, fdv]
   */
  public injectFreedataValue(e: AlkEvent): void {
    // TODO TODO injecter aussi la date de départ
    const sourceFei: FeiVO = e.data[0] as FeiVO;
    const fdv: FreedataValueVO = e.data[1] as FreedataValueVO;
    if(this.state >= WIDGET_STATE.READY) {
      if(!!fdv) {
        const fdKey: string = fdv.freeDataCode + ViewData.CONTEXT_SEPARATOR + fdv.level;
        // console.log('fastSelect', this.context.fdk, fdKey, fdv);
        if(fdKey === this.context.fdk) { // vérifier que c'est le même FD et même Level
          this.doSelect(fdv);
        }
      } else {
        let res:ConfirmationResultObj = new ConfirmationResultObj();
        res.result = CONFIRMATION_RESULT.CONFIRM;
        this.askForAdd(res);
      }
    }
  }

  /**
   * rinçage de la sélection, sur doSelect à vide ou Init
   */
  private reset(): void {
    this.currentFDV = null;
    this.fdvDS.clearSelection();
    this.resetClone();
  }

  /** 
   * sélection réelle, possible passage à "pas de sélection"
   * ou possible FDV "carcasse vide" en mode création
   * @param fdv sélectionné
   */
  private doSelect(fdv: FreedataValueVO): void {   
    //console.log("fdform doselct", fdv);
    this.saveDisabled = true;
    this.hasUpdate = false;

    if(this.state < WIDGET_STATE.READY) {
      return; // pas prêt
    }

    if(!fdv) { // déselection, en fait
      this.reset();
      this.alterContent(WIDGET_ACTION_ORIGIN.SELECT); // pour refresh graphique
      return;
    }


    if(fdv === this.currentFDV) { // self
      return; // on fait rien
    }

    this.currentFDV = fdv;
    this.fdvDS.select([this.currentFDV]); 
    this.feedClone(this.currentFDV); // creation du clone de travail

    this.alterContent(WIDGET_ACTION_ORIGIN.SELECT);
  }

  /**
   * remise à 0 des valeurs de travail
   */
  private resetClone(): void {
    this.clone = null;
  }

  /**
   * alimentation du clone de travail
   * @param fdv 
   */
  private feedClone(fdv: FreedataValueVO): void {
    this.clone = this.model.freedataEngine.cloneFDV(fdv); // création du clone de travail
    if(!this.clone.id) { // mode création
      this.feedDefaultKeys(null, true); // alimentation des champs par défaut
    } else {
      this.feedPerpetual(null);
    }
  }

  /* ##############################################################################
        cycle de vie et construction graphique
  ############################################################################## */

  ngAfterViewInit() {
    super.ngAfterViewInit();
    /** modification de n'importe quel élément, dispatché par les ffRenderer) */
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.FORM_UPDATE, this.fieldUpdate);
    /** saisie en cours sur numeric ou datefield */
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.FORM_KEY, this.keyUpdate);

    /** Sélection externe sur 2x clic dans un pivot */
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.PIVOT_DOUBLE, this.fastSelect);
    /** injection par programmation */
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.PROGRAMMATIC_FEED, this.fastSelect);
    /** déselection externe forcée */
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.UNSELECT, this.askForUnselect);
    /** ouverture / fermeture d'un accordéon */
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.FORM_ZONE_UPDATE, this.manageContainers);
    
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.FORM_UPDATE);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.FORM_KEY);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.PIVOT_DOUBLE);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.PROGRAMMATIC_FEED);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.UNSELECT);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.FORM_ZONE_UPDATE);
  }

  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); // je cherche un master. S'il y en a un, READY n'est pas suffisant
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }
    
    if(this.state === WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    this.drawOrRefresh(origin, data);
    super.alterContent(origin, data);
  }

  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    // console.log('FD FORM drawOrRefresh', origin, this.state, data);
    // console.log('FDFORM drawOrRefresh', origin, data);
    let mustResetSelection: boolean = false; // on est obsolète, on rince
    let mustBuildRows: boolean = false; // on doit construire les lignes
    let mustDrawFields: boolean = false; // on doit construire les éléments graphiques
    let mustRefresh: boolean = false; // simple refresh sur recalcul

    // première création
    if(origin === WIDGET_ACTION_ORIGIN.INIT) {
      // mustBuildRows = true;

      // alimentation du level et du code
      this.freedataCode = this.context.fdk.split(ViewData.CONTEXT_SEPARATOR)[0];
      this.level = Number(this.context.fdk.split(ViewData.CONTEXT_SEPARATOR)[1]);
      this.freedata = this.model.freedataEngine.getFreeData(this.freedataCode);
      // alimentation de la map de champs pour ce FD
      this.fieldsMap = new Map(this.freedata.allFieldsAsArgs.map(item => [item.code, item]));
      // alimentation de la matrice de triggers
      
      // FEI nécessaire pour les calculs inter-champs
      if(this.fei) { // step lié, on prend son FEI
        this.fakeFei = this.model.dataEngine.generateFakeFEI(this.fei) as FeiVO;  
      } else { // sinon, le 1er dispo
        let tmpFEI: FeiVO  = this.model.dataEngine.getFei(COMPUTE_MODE.LOCAL, 1, ViewData.DEFAULT_SCENARIO) as FeiVO;
        if(!tmpFEI) {
          tmpFEI = this.model.dataEngine.generateFei(this.model.currentVision, 
            this.model.currentVision.visionSteps[1], 
            ViewData.DEFAULT_SCENARIO, this.context, true);
        }
        if(!!tmpFEI) {
          this.model.measureEngine.buildFeiMatrix(tmpFEI);
          this.fakeFei = this.model.dataEngine.generateFakeFEI(tmpFEI) as FeiVO;  
        }

        
      }
      // console.log('FD form FEI', this.fakeFei);
      
      this.matrix = this.model.freedataFormulaCoreEngine.buildTriggerMatrix(this.fieldsMap, this.fakeFei);
      
      // console.log('matrix', this.matrix);

      // prise des fonctions de contrôle pour collecte mandatory
      let requiredArgs: string[] = [];
      [...this.fieldsMap.values()].forEach((fdavo: FreedataArgsVO) => {
        if (!!fdavo.validator && fdavo.validator.indexOf(FreedataFormulaCoreEngine.MANDATORY) > -1) {
          requiredArgs.push(fdavo.code);
        }
      });
      this.requiredFields = new Set(requiredArgs.concat(this.baseRequiredFields));
      if(this.level > 1) {
        this.requiredFields.add(WizConstants.FIELD_PARENT_CODE); // parentCode nécessaire si level > 1
      }

    }

    if(origin === WIDGET_ACTION_ORIGIN.READY) {
      mustBuildRows = true;
      mustResetSelection = true;
      mustDrawFields = true;
      this.feedGroups();
      if(!!WizRoot.mobileMode || this.gatlingMode) { // mode mobile ou gatling, nouveau d'office
        setTimeout(() => {
          // if there is no modification
          let res:ConfirmationResultObj = new ConfirmationResultObj();
          res.result = CONFIRMATION_RESULT.CONFIRM;
          this.askForAdd(res);
        }, 0);
        
      }
    }

    // modification de la configuration
    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {
      mustDrawFields = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.OUTER_SELECT) {
      mustBuildRows = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.RECOMPUTE) {
      let check: string = this.syncFormDS(data);
      if(check === 'DELETED') {
        mustResetSelection = true;
      } else if(check === 'REFRESH') {
        //console.log('FD FORM recompute', origin, data);
        mustBuildRows = true; 
      }


    }

    // changement de sélection
    if(origin === WIDGET_ACTION_ORIGIN.SELECT) {
      mustDrawFields = true;
    }

    // modification du filtre... on vérifie la capacité de création
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_FILTERING) {
      //console.log('globalFiltering on FDForm');
      if(this.mandatoryGlobalEmpty()) {
        mustResetSelection = true;
      }
      this.cdref.markForCheck();
    }


    if(mustResetSelection) {
      this.reset();
    }
    if(mustBuildRows) {
      this.buildLines(); // délégué au WidgetBasic
      // console.log('FDFORM mustBuildRows', this.creationMode, this.currentFDV, this.fdvDS?.getWorkedSource());
      
      if(!this.creationMode) {
        let exist: FreedataValueVO = null;
        // FDV en cours
        if(this.currentFDV) { // vérifier qu'on est toujours dans le pool
          exist = this.fdvDS.getItemByKey(this.clone.code) || null;

          // en cas de sélection sur le master autre que moi, exist = false (je ne suis plus dans le pool)

          // modif externe sur moi, plus récent
          if(!!exist && !!this.currentFDV && 
                    exist.code === this.currentFDV.code 
                    && exist.version > this.clone.version) { // prise de version sur le CLONE, le FDV est synchronisé
                    // modifié par un autre, on force le select
                    this.doSelect(exist);
          }
        } 
          
        // plus dans le pool, je cherche l'unique
        if(!exist) {
          if(this.fdvDS?.getWorkedSource()?.length === 1) { // auto select si un seul élément
            exist = this.fdvDS.getWorkedSource()[0] ; // autoselect si 1 seul élément
          } 

          if(exist) { // il existe
            this.doSelect(exist);
          } else {
            this.doSelect(null);
          }
        }


        // if(!!exist && !!this.currentFDV && 
        //   exist.code === this.currentFDV.code 
        //   ) {
        //   console.log('me on compositeReload', this.currentFDV);
          
        // }
        // if(!!exist && !!this.currentFDV && 
        //   exist.code === this.currentFDV.code 
        //   && exist.version > this.currentFDV.version) {
        //   // modifié par un autre, on force le select
        //   this.doSelect(exist);
        // }
        // if(!exist) {
        //   this.doSelect(null);
        // }
      } else { // mode création, je viens de créer mon FDV     
        let exist: FreedataValueVO = this.fdvDS.getItemByKey(this.clone.code) || null;
        if(exist) {
          this.doSelect(exist);
        }
      }  
      
    }

    // modification externe sur l'objet en cours d'édition
    if(mustRefresh) {
      let res:ConfirmationResultObj = new ConfirmationResultObj();
      res.result = CONFIRMATION_RESULT.CONFIRM;
      this.doCancel(res);
    }

    if(mustDrawFields) {
      // construction graphique, on créé les champs de formulaire
      const widItems: GridsterWidgetItem[] = []; // les champs à construire
      const ffs: WidgetConfigurationItemObj[] = this.context.ff; // la conf gridster : les champs à afficher dans le form

      // en passant par le composant bl-gridster, on va instancier des ffRenderer
      if (!!ffs && !!this.clone) { // pour chaque champ, mais seulement si j'ai un clone... (sinon écran vide)
        ffs.forEach((wcio: WidgetConfigurationItemObj) => {
          const arg: FreedataArgsVO = this.fieldsMap.get(wcio.fdvf); // va servir de matrice, c'est la structure
          if (!!arg) {
            if(arg.code === WizConstants.FIELD_CODE) { // on utilise le code
              this.useShortCodeAsCode = false;
            }
            const result: GridsterWidgetItem = new GridsterWidgetItem(wcio, new PivotField(this, wcio, null, arg)); 
            widItems.push(result);
          } else if(ViewData.isFreeField(wcio.fdvf)) {
            const result: GridsterWidgetItem = new GridsterWidgetItem(wcio, new PivotField(this, wcio, null, null)); 
            widItems.push(result);
          } else {
            console.log('wrong widget item', wcio);
          }
        });
      }

      this.widgetItems = widItems; // pack d'item pour le gridster

      // this.fieldUpdate(null); // contrôle de editRestrict & valueControl
      // WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.FORM_REFRESH_FIELDS, this)); // dispatch de l'event pour controle par les compos

      setTimeout(() => {
        this.checkIntegrity(); // contrôle d'intégrité
        this.cdref.markForCheck();
      }, 0);
    }
  }

  /* ##############################################################################
        saisie dans le formulaire, complétions et contrôles
  ############################################################################## */

  /** annulation des modifs */
  public askForCancel(e: WizEvent): void {
    
    if (this.hasUpdate) {
        // True : le modèle à changé
        // ouverture de la popup
        // l'Observer est passé en tant que data
        WizPopUp.confirm(
          '$.cancel.update.pop.title.$',
          '$.cancel.update.pop.content.$',
          this.doUnselect.bind(this),
          CONFIRMATION_MODE.CONFIRM_CANCEL,
          null
        ).show();
      } else {
        // if there is no modification
        let res:ConfirmationResultObj = new ConfirmationResultObj();
        res.result = CONFIRMATION_RESULT.CONFIRM;
        this.doCancel(res);
    }
  }

  public doCancel(confirmationResult: ConfirmationResultObj): void {
    if (confirmationResult.result === CONFIRMATION_RESULT.CONFIRM) {
          this.saveDisabled = true;
    this.hasUpdate = false;

    this.feedClone(this.currentFDV); // creation du clone de travail
    this.alterContent(WIDGET_ACTION_ORIGIN.SELECT); // resélection

    // on signale au fields qu'ils doivent se mettre à jour
    WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.FORM_REFRESH_FIELDS, null));
    }
  }


  /**
   * suppression en cascade pour les args de même FD avec level >
   */
  private deleteCascade(updateFields: Set<string>): void {
    
    // map des Args modifiés par datas.FD = data.level (on prend le min level pour nettoyer tout au "dessus")
    let toDieMap: Map<string, number> = new Map();
    [...updateFields.keys()].forEach(k => {
      const updatedArg: FreedataArgsVO = this.fieldsMap.get(k);
      if(updatedArg.dataType === FIELD_KIND.OBJECT) {
        // console.log('updatedArg', updatedArg.code, updatedArg.datas);
        let parts: string[] = updatedArg.datas.split(WizConstants.CONTEXT_SEPARATOR);
        let code: string = parts[0];
        let lvl: number = Number(parts[1]);
        toDieMap.set(code, lvl);
      }
    });
    
    // console.log('deleteCascade', updateFields);
    [...this.fieldsMap.keys()].forEach((key: string) => { // pour chaque champ
      const fdavo: FreedataArgsVO = this.fieldsMap.get(key);
      if(fdavo.dataType === FIELD_KIND.OBJECT) {
        
        let parts: string[] = fdavo.datas.split(WizConstants.CONTEXT_SEPARATOR);
        let code: string = parts[0];
        let lvl: number = Number(parts[1]);
        if(toDieMap.has(code) && toDieMap.get(code) < lvl) { // ancêtre modifié
          // console.log('todie', fdavo.code, code, lvl);
          this.model.freedataEngine.feedFreedataValueField(this.clone, key, null );
          updateFields.add(fdavo.code);
        } 
      }
    });
  }



  /** recalcul pour les éléments à recalculer (à chaque fois) */
  private feedPerpetual(updateFields: Set<string>): void {
    [...this.fieldsMap.keys()].forEach((key: string) => { // pour chaque champ
      const fdavo: FreedataArgsVO = this.fieldsMap.get(key);
      if(!!fdavo.valor && fdavo.valorTrigger?.indexOf(ViewData.TRIGGER_ALWAYS) > -1){// } && !this.clone.argsObjVO.hasOwnProperty(key)) { // seulement si une valeur par défaut et PAS instancié

         let result: string = this.model.freedataFormulaCoreEngine.computeFreedataExp(this.clone, fdavo.valor, this.fakeFei).valor || null;
         this.model.freedataEngine.feedFreedataValueField(this.clone, key, result );
         if(updateFields) {
          updateFields.add(fdavo.code);
         }
      }
    });
  }

  /**
   * éventuelle alimentation des champs déclenchés par un trigger
   * Appelé sur init et sur modification d'un champ
   * @param data issu du ArgRenderer
   * @param initMode en cas d'appel depuis feedClone
   * @returns le set de champs modifiés
   */
  private feedDefaultKeys(data: ArgEditorData, initMode: boolean = false): Set<string> {
    
    let toUpdateFields: string[] = []; // les à calculer / modifier
    
    if(initMode) { // tentative d'alimentation des valeurs par défaut en création
      [...this.fieldsMap.keys()].forEach((key: string) => { // pour chaque champ
        const fdavo: FreedataArgsVO = this.fieldsMap.get(key);
        if(!!fdavo.valor && !this.clone.argsObjVO.hasOwnProperty(key)) { // seulement si une valeur par défaut et PAS instancié
           
        // injection de valeur depuis user ou globalFilter
        if(fdavo.valor.indexOf(WizConstants.INJECT_USER_KEY) > -1 || 
          fdavo.valor.indexOf(WizConstants.INJECT_GLOBAL_KEY) > -1
          ) {

            // d'abord user
            if(fdavo.valor.indexOf(WizConstants.INJECT_USER_KEY) > -1) {
              if (this.model.widgetEngine.userGlobalFilter.has(fdavo.code)) {
                let userResSet: Set<string> = this.model.widgetEngine.userGlobalFilter.get(fdavo.code);
                let userRes = [...userResSet.keys()][0];
                this.model.freedataEngine.feedFreedataValueField(this.clone, key, userRes );
                //toUpdateFields.push(fdavo.code);
                // console.log('userInject', userRes);
              }


            } 
            // puis global (plus précis)
            if(fdavo.valor.indexOf(WizConstants.INJECT_GLOBAL_KEY) > -1) {
              let keys = this.model.widgetEngine.globalFilterKeys;
              // console.log('keys', keys);
              if (keys.has(fdavo.datas)) {
                let userResSet: Set<string> = keys.get(fdavo.datas); // TODO TODO ce sont des shortCodes...
                let userRes = [fdavo.datas.split(WizConstants.CONTEXT_SEPARATOR)[0], [...userResSet.keys()][0]].join(WizConstants.CONTEXT_SEPARATOR);
                this.model.freedataEngine.feedFreedataValueField(this.clone, key, userRes );
                
                // console.log('globalInject', userRes);
              }
            }
            toUpdateFields.push(fdavo.code);

          } else {
            let result: string = this.model.freedataFormulaCoreEngine.computeFreedataExp(this.clone, fdavo.valor, this.fakeFei).valor || null;
            // PAS les injections 
            if(!result || result.indexOf(WizConstants.INJECT_RESPONSE_KEY) < 0) {
              this.model.freedataEngine.feedFreedataValueField(this.clone, key, result );
              // his.clone.addArg(key, FreedataFormulaCoreEngine.computeFreedataExp(this.clone, fdavo.valor).valor || null);
              toUpdateFields.push(fdavo.code);
            }
          }

        }

      });


    } else { // transmission depuis fieldUpdate
      toUpdateFields.push(data.arg.code);
    }

    // mémorisation des champs modifiés
    let reallyUpdatedKeys: Set<string> = new Set([...toUpdateFields]);

    // on boucle sur les champs à recalculer
    while (toUpdateFields.length > 0) { // TODO TODO ONCE Sur SELF
      const currentUpdate: string = toUpdateFields.pop();
      if(this.matrix.has(currentUpdate)) { // on cherche l'entrée dans la matrice
        
        let children: Set<TriggerMatrixItem> = new Set(this.matrix.get(currentUpdate).children);
        let pendingChildren: Set<TriggerMatrixItem> =new Set();
        // console.log('update', currentUpdate, this.matrix, children);
        while(children.size > 0) {
          
          pendingChildren = new Set(); // les sub-deps
          // pour toutes les dépendances
          [...children.values()].forEach((tmi: TriggerMatrixItem) => { 
            
            // on ajoute les dépendances pour le prochain tour de calcul
            tmi.children.forEach((child: TriggerMatrixItem) => {
              if(child.code === currentUpdate) { // PATCH gestion de l'injection de valeur dans un autre champ (Num vers shortCode)
                //console.log('self ?', tmi);
              } else {
                pendingChildren.add(child);
              }
            })

            // TODO ici détecter injection (prise de la fonction dans formulaCore)
            
            
            // et on calcul
            let result: string = this.model.freedataFormulaCoreEngine.computeFreedataExp(this.clone, tmi.fdavo.valor, this.fakeFei).valor || null;
            let realResult: string = result;
            let targetField: string = tmi.code;
            
            // interception des injections
            // le résult va contenir CHAMP @@INJECT@@|cible|valeur => remplacer tmi.code par cible
            if(result?.indexOf(WizConstants.INJECT_RESPONSE_KEY) > -1) {
              let parts: string[] = result.split(WizConstants.KEY_SEPARATOR);
              realResult = parts[2];
              targetField = parts[1];

              // console.log('INJECT on update', this.clone, result);
            }
            
            // console.log('child targetField', targetField, result);
            let targetArg: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(this.freedataCode, targetField);
            if(targetArg.dataType === FIELD_KIND.OBJECT) {
              if(realResult?.indexOf(WizConstants.CONTEXT_SEPARATOR) > 0) { // résultat objet, on prend seulement la 2ème partie
                realResult = realResult.split(WizConstants.CONTEXT_SEPARATOR)[1];
              }
              realResult = [targetArg.datas.split(WizConstants.CONTEXT_SEPARATOR)[0], realResult].join(WizConstants.CONTEXT_SEPARATOR);
            }
            // console.log('child targetField2', targetField, result, realResult);
            this.model.freedataEngine.feedFreedataValueField(this.clone, targetField, realResult );
            // this.clone.addArg(tmi.code, );
            
            reallyUpdatedKeys.add(targetField); // mémorisation pour refresh des argRenderers
          });

          children = new Set(pendingChildren); // on joue les dépendances suivantes
        }

      }
    }

    // console.log('reallyUpdatedKeys', reallyUpdatedKeys);
    return reallyUpdatedKeys;
  }

  /**
   * une modif en cours sur un compo type dateField ou numéric, on affiche save & cancel
   * @param e 
   */
  private keyUpdate(e:WizEvent): void {
    this.hasUpdate = true;
    // console.log('FDFORM keyUpdate', e);
    this.checkIntegrity();
  }

  /** 
   * contrôle de cohérence du clone
   * déclenché sur init et sur modif de champs
   * Active ou désactive le bouton save
   * @returns TRUE en cas d'erreur (et oui, c'est chelou)
   */
  private checkIntegrity(): boolean {
    if(!this.clone) { // seulement si un clone, aucun intérêt sinon
      return;
    }

    let mustBeDisabled: boolean = !this.hasUpdate; // ViewData.wizUpdateModel; // contrôle de cohérence pour activation du save
    Object.keys(this.clone.argsObjVO).forEach((key: string) => {
      if(this.requiredFields.has(key)) {
        if (!this.clone.argsObjVO[key] || this.clone.argsObjVO[key].length <= 0) {
          mustBeDisabled = true;
        }
      }
    });
   
    // if (!mustBeDisabled) { // les champs requis sont OK, on contrôle les validators
    this.errorSet = new Map();
    this.restrictSet = new Map();
    this.lockSet = new Map();

    // contrôle des restrictions (supprime les valeurs non éditables)
    [...this.fieldsMap.values()].forEach((fdavo: FreedataArgsVO) => { // pour chaque champ
        
      // contrôle des restrictions
      if(!!fdavo.restrict) { // en cas de validateur 
        const neoExp = fdavo.restrict;
        
        let result: boolean = this.model.freedataFormulaCoreEngine.checkFreedataExp(this.clone, neoExp, this.fakeFei);
        if (!result) {
          this.restrictSet.set(fdavo.code, fdavo.trsEditRestrictMessage);
        }
      }

      // contrôle des restrictions
      if(!!fdavo.lock) { // en cas de validateur 
        const neoExp = fdavo.lock;
        
        let result: boolean = this.model.freedataFormulaCoreEngine.checkFreedataExp(this.clone, neoExp, this.fakeFei);
        if (!result) {
          this.lockSet.set(fdavo.code, fdavo.trsEditRestrictMessage);
        }
      }
    });

    // contrôle des valeurs
    let errorFields: Set<string> = new Set();
    [...this.fieldsMap.values()].forEach((fdavo: FreedataArgsVO) => { // pour chaque champ
      if(!!fdavo.validator) { // en cas de validateur
        
        // calcul optimisé sur OR / AND
        const neoExp = fdavo.validator;

        let result: boolean = this.model.freedataFormulaCoreEngine.checkFreedataExp(this.clone, neoExp, this.fakeFei);
        // if(fdavo.code === "DFSES") {
        //   console.log('fd validator', fdavo.code, fdavo.validator, result);
        // }
        
        if (!result) {
          errorFields.add(fdavo.code);
          this.errorSet.set(fdavo.code, fdavo.trsValueControlMessage);
        }
      }

      if(fdavo.minValue !== null) { // contrôle des mins 
        const val: number = this.clone.argsObjVO[fdavo.code] ? Number(this.clone.argsObjVO[fdavo.code]) : null;
        if(val !==null && val < fdavo.minValue) {
          errorFields.add(fdavo.code);
          this.errorSet.set(fdavo.code, WizTranslationManager.getInstance().translateKey('validators.numberValidator.lowerThanMinError', [fdavo.minValue.toString()]));
        }
      }

      if(fdavo.maxValue !== null) { // contrôle des maxs 
        const val: number = this.clone.argsObjVO[fdavo.code] ? Number(this.clone.argsObjVO[fdavo.code]) : null;
        if(val !==null && val > fdavo.maxValue) {
          errorFields.add(fdavo.code);
          this.errorSet.set(fdavo.code, WizTranslationManager.getInstance().translateKey('validators.numberValidator.greaterThanMaxError', [fdavo.maxValue.toString()]));
        }
      }


    });

    if( this.errorSet.size > 0) {

      WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.FORM_REFRESH_ERRORS, errorFields));       
      mustBeDisabled = true;
    }
    
    this.saveDisabled = mustBeDisabled;

  }

  /**
   * modification d'un élement de champ du formulaire
   * @param e ALK_EVENT_TYPE.FORM_UPDATE {field: ArgEditorData, value: string}
   */
  private fieldUpdate(e: AlkEvent): void {
    if(!this.clone) { // pas encore d'objet édité
      return;
    }
    
    // console.log('fieldUpdate', (e.data.field as ArgEditorData).arg.code, e.data.value);

    // modification du champ cible
    this.model.freedataEngine.feedFreedataValueField(this.clone, (e.data.field as ArgEditorData).arg.code, e.data.value);
    // console.log('fieldUpdate', (e.data.field as ArgEditorData).arg.code, e.data.value, this.clone);
    
    // mise à jour en cascade par les triggers
    let updateFields: Set<string> = this.feedDefaultKeys(e.data.field);
    
    
    // suppression en cascade des valeurs sur même FD pour les args
    this.deleteCascade(updateFields);  


    // mise à jour en cascade par les formules permanentes
    this.feedPerpetual(updateFields);

    if(updateFields.size > 0) {
      // console.log('fieldUpdate updateFields', e);
      this.hasUpdate = true; // on marque l'update (avant le contrôle d'intégrité)
      this.checkIntegrity(); // contrôle d'intégrité
    }

    // event pour refresh les argRenderer
    WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.FORM_REFRESH_FIELDS, updateFields));
    
    if(this.gatlingMode) {   
      if((e.data.field as ArgEditorData)?.arg?.dataType === FIELD_KIND.BINARY_MULTI) {
        // console.log('save on gatling', this.clone);
        this.save();
      } 
      //else {
      //   // localStorage.setItem('gatlingParams', JSON.stringify(
      //   //   this.clone.source
      //   // ));
      // }
    }
  }



  /* ##############################################################################
      validation
  ############################################################################## */

  /** validation du contenu */
  public doValid(e: WizEvent=null): void {
    
    if(!this.clone.exist) { // création 
      if(this.useShortCodeAsCode) { // on utilise le shortCode comme code
        this.clone.code = this.clone.shortCode;
      }
      this.checkUnicity(); // contrôle d'unicité pour pas tout perdre
    }
    else { // simple update
      this.save();
    }
  }

  /** contrôle remote d'unicité sur le code en cas de création */
  private checkUnicity(): void {
    // TODO plutôt faire un sync sur Freedata et contrôler en local
    new FreeDataValueUnicityCmd(this.model).checkUnicity(this.clone, this.backFromCheckUnicity.bind(this)).execute();
  }

  /**
   * retour de service de contrôle d'unicité
   * @param cmd 
   */
  private backFromCheckUnicity(cmd: FreeDataValueUnicityCmd): void {
    if(cmd.result.entities.length > 0) {
      const exist: FreedataValueVO = this.model.freedataEngine.newFreedataValueVO(cmd.result.entities[0]);
      this.model.freedataEngine.niceErrorMessage('$.sync.error.updateonupdate.$', this.clone.code, exist.trsLabel, exist);
    } else {
      this.save(); // c'est bon on a passé le contrôle
    }
  }

  /**
   * persistance
   */
  public save(): void {
  
    this.saveDisabled = true;
    this.freedata.resetUpdateMap(); // on vide l'historique sur le FD, au cas où...
    // mise à jour de l'update, c'est lui qui sert à détecter les écarts de sync
    if (!this.clone.exist) { // existe pas, c'est un create
      this.freedata.addToUpdatePack(this.clone, EM_ACTION.CREATE);
    } else {  
      this.freedata.addToUpdatePack(this.clone, EM_ACTION.UPDATE);
    }

    // on regarde si on gère les baselines, si oui, création sur le step
    // le FDV doit porter tous les champs de la clé de baseline, donc potentiellement des args de type OBJECT
    // donc possibilité de création, update en cas de modifs des args
    let updates: UpdateEntry[] = null;
    if(!!this.fei) { // step asservi
      // injection du clone pour prise des bonnes valeurs sur le FDV
      this.fei.fakeFDV = this.clone;
      updates = this.model.freedataEngine.generateSlaveBaselines(this.freedata, this);
      this.fei.fakeFDV = null;

    }

    let fdMap: Map<string, FreeDataVO> = new Map();
    fdMap.set(this.freedata.code, this.freedata);

    const csp: CompositeSourcePack = {
      freedataManagement: true,
      freedata: fdMap, // update / create / delete sur FDV
      updates, // maj sur des Baselines
    }

    this.model.compositeEngine.compositeCommit(csp, this.backFromComposite.bind(this));
  }

  /**
   * retour de service de commit, synchro du clone (sur delta create avec master)
   * @param cmd 
   */
  private backFromComposite(csp: CompositeSourcePack): void {

    // WizPopUp.toast('backFromComposite', WizIcon.TOAST_SUCCESS).show();
    if(this.gatlingMode) {
      let realFD: FreeDataVO = [...csp.freedata.values()][0];
      let realFDV: FreedataValueVO = realFD.getFreeDataValue(this.clone.code);
      // this.currentFDV = realFDV;
      // console.log('gatling back from composite', realFDV);
      // mémorisation dans le cache local
      // LMA TODO TODO ??
      localStorage.setItem('gatlingParams', JSON.stringify(
        this.clone.source
      ));
      
      this.doSelect(realFDV);
    } else {
      let realFD: FreeDataVO = [...csp.freedata.values()][0]; // csp.freedata;
      let realFDV: FreedataValueVO = realFD.getFreeDataValue(this.clone.code);
      this.feedClone(realFDV); // on force le FDV créé
    }
    

  }

  /* ##############################################################################
      validation
  ############################################################################## */

  /** annulation de la sélection (retour à vide) */
  public askForUnselect(e: any = null): void {
    
    if (this.hasUpdate) {
        // True : le modèle à changé
        // ouverture de la popup
        // l'Observer est passé en tant que data
        WizPopUp.confirm(
          '$.cancel.update.pop.title.$',
          '$.cancel.update.pop.content.$',
          this.doUnselect.bind(this),
          CONFIRMATION_MODE.CONFIRM_CANCEL,
          null
        ).show();
      } else {
        // if there is no modification
        let res:ConfirmationResultObj = new ConfirmationResultObj();
        res.result = CONFIRMATION_RESULT.CONFIRM;
        this.doUnselect(res);
    }
  }

  public doUnselect(confirmationResult: ConfirmationResultObj): void {
    if (confirmationResult.result === CONFIRMATION_RESULT.CONFIRM) {
      this.doSelect(null);
    }
  }

  /** click sur l'icone de suppression */
  public askForDelete(): void {
    let width: string = '400px';
    let height: string = '350px';

    if(!this.currentFDV.exist) { // c'est une créa, on reset simplement
      this.currentFDV = null;
      this.drawContent();
      return;
    }

    let params: DeleteFreedataValueParams = {fdv: this.clone, widget: this, callback: this.doDelete.bind(this)};


    const deletePop: WizPopUp = new WizPopUp(FreedataGridDeletePop, false, false, null, width, height, 
      params,
    );
    const pos: DOMRect = this.icoDelete.elementRef.nativeElement.getBoundingClientRect();
    const bestXY: [number, number] = PopUtils.setPopCoord(pos, deletePop, WIZ_PHYSIC_POSITION.BOTTOM_RIGHT);
    const x: string = bestXY[0] + 'px';
    const y: string = bestXY[1] + 'px';
    const targetPosition: any = { left: x, top: y }; // position physique
    deletePop.customPosition = targetPosition;

    if (PopUpManager.exist(deletePop)) {
      // suppression de l'instance existante
      deletePop.hide();
    }
    // ouverture de la pop. Data[fei, context, WizbaseCollector, PivotField, callback]
    deletePop.show();
  }

  /** suppression autorisée */
  public mayDelete(): boolean {
    if (!this._context?.eda) { // widget en lecture seule
      return false;
    }

    if (!this.currentFDV) { // pas de sélection
      return false;
    }
    return true;
  }

  /** demande d'export depuis le bouton */
  public exportDatas(kind?: string): void {
    if(!kind || kind === 'excel') {
      // pas d'export Excel pour lui this.model.excelExportEngine.exportAsExcel('allkeyme', this);
    } else {
      this.model.excelExportEngine.exportAsPdf('allkeyme', this, this.argsZone.elementRef, null, null);
    }
  }

  /** demande de clonage */
  public askForClone(): void {
    
    // console.log('askForClone');
    //prise des champs exclus du clonage
    let excludeSet: Set<string> = new Set();
    let inSet: Set<string> = new Set();
    this.context.ff.forEach(wcio => {
      if(wcio.noc) {
        excludeSet.add(wcio.fdvf);
      } else {
        inSet.add(wcio.fdvf);
      }
    });

    let oldDie: FreedataValueVO = this.currentFDV;
    
    
    let res:ConfirmationResultObj = new ConfirmationResultObj();
    res.result = CONFIRMATION_RESULT.CONFIRM;
    this.askForAdd(res);


    [...inSet.keys()].forEach(s => {
      // modification du champ cible
      this.model.freedataEngine.feedFreedataValueField(this.clone, s, oldDie.argsObjVO[s]);
    });
  }

  /* ##############################################################################
      gestion des groupes de champs
  ############################################################################## */

  // map des groupes masqués
  public hiddenGroups: Map<string, boolean> = new Map();

  private feedGroups(): void {
    this.context?.sel?.forEach(ws => {
      this.hiddenGroups.set(ws.k, true);
    });
  }

  public manageContainers(e: AlkEvent): void {
    let data: ArgEditorData = e.data.data;
    let state: boolean = e.data.value; // true = masqué
    let grp: string = data.context.source.pgrp;
    
    let realChange: boolean = false;
    if(!this.hiddenGroups.has(grp) && !!state) {
      realChange = true;
    } else {
      if(!!state && !!this.hiddenGroups.get(grp)) {
        // 2 positifs
      } else if (!state && !this.hiddenGroups.get(grp)) {
        // 2 négatifs
      } else {
        realChange = true;
      }
    }

    this.hiddenGroups.set(grp, state);

    if(realChange) {
      const hiddens: WidgetSelect[] = [];
      [...this.hiddenGroups.keys()].forEach((key: string) => {
        if(!!this.hiddenGroups.get(key)) {
          hiddens.push({k:key, v:"1"});
        }
      });

      this.context.sel = hiddens; 
      // console.log('form manageContainers', this.context.sel);
      this.updateContext(this.context, true);
    }
  }

  /**
   * sur retour de commit, on check si je suis dans le pack modifié
   * @param data: DataPack
   * @return true si delete ou create, on va rebuild les lignes dans ce cas
   */
  public syncFormDS(data: DataPack): string {

    let result: string = null;

    if(!!this.currentFDV?.exist) {

      data.deletedFreedataAndValues?.forEach(emt => {
        if(emt.id === this.currentFDV.id) {
          result = 'DELETED';
          return;
        }
      });

      data.inFreedataValues?.forEach(fdv => {
      if(fdv.id === this.currentFDV.id) {
          let src: FreedataValueVO = this.fdvDS.getItemByKey(fdv.code);
          if(src) {
            src.source = fdv;
            // console.log('syncD on Form', src, this.fdvDS);
            result = 'REFRESH';
            return; // on sort de la boucle
          }

        }
      });
      
    }
    return result;
  }


}

